import {
  Header,
  HeaderDescription,
  HeaderTitle,
  Input,
} from '@startupquickstart/frontend';
import { Search } from 'lucide-react';

interface GuestListSearch {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

interface GuestListHeaderProps {
  title: string;
  description: string;
  /**
   * Optional compact search box, rendered bottom-aligned on the right exactly
   * like the filter-less CRUD list headers on the hotel tabs. Omit to render
   * just the title block.
   */
  search?: GuestListSearch;
}

/**
 * Section header for the custom (non-CrudListView) lists on the guest detail
 * tabs. Mirrors the hotel tabbed lists' header: a title + "Manage…" description
 * on the left and a compact, bottom-aligned search box on the right, so the
 * guest tabs match the rest of the hotel screens.
 *
 * @param title - The list title (e.g. "Scans").
 * @param description - The supporting line under the title.
 * @param search - Controlled search box; omit to hide it.
 */
export function GuestListHeader({ title, description, search }: GuestListHeaderProps) {
  return (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
      <Header>
        <HeaderTitle>{title}</HeaderTitle>
        <HeaderDescription>{description}</HeaderDescription>
      </Header>
      {search && (
        <div className="relative w-full md:w-[300px] md:shrink-0 md:self-end md:ml-auto">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search.value}
            onChange={(e) => search.onChange(e.target.value)}
            placeholder={search.placeholder ?? `Search ${title.toLowerCase()}...`}
            className="pl-9"
            aria-label={`Search ${title.toLowerCase()}`}
          />
        </div>
      )}
    </div>
  );
}
